import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Hero() {
  const { t } = useTranslation('refection');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  const navigateTo = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      ref={ref}
      id="hero"
      className="relative min-h-[500px] md:min-h-[600px] flex items-center bg-slate-primary overflow-hidden pt-20"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20close-up%20of%20a%20residential%20roof%20replacement%20project%20in%20progress%20on%20a%20Canadian%20suburban%20house%20showing%20old%20worn%20shingles%20being%20removed%20and%20new%20dark%20grey%20asphalt%20shingles%20being%20installed%20professional%20roofing%20crew%20at%20work%20golden%20hour%20warm%20light%20high%20quality%20photography%20copper%20and%20slate%20color%20tones&width=1600&height=800&seq=refhero1&orientation=landscape"
          alt="Réfection de toiture à Lévis — couvreurs partenaires certifiés RBQ au travail"
          className="w-full h-full object-cover object-center"
          loading="eager"
          fetchpriority="high"
          width="1600"
          height="800"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-primary/90 via-slate-primary/70 to-slate-primary/50" />
      </div>

      <div className="relative w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className={`flex items-center gap-2 text-sm mb-6 md:mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <button
              onClick={() => navigateTo('/')}
              className="text-white/60 hover:text-copper transition-colors"
            >
              {t('breadcrumb_home')}
            </button>
            <i className="ri-arrow-right-s-line text-white/40 text-xs" />
            <span className="text-white/80">{t('breadcrumb_current')}</span>
          </nav>

          {/* H1 */}
          <h1
            className={`font-manrope font-extrabold text-white text-[40px] sm:text-4xl md:text-5xl lg:text-[56px] leading-tight mb-4 md:mb-6 transition-all duration-1000 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {t('hero_h1')}
          </h1>

          {/* Subtitle */}
          <p
            className={`text-white/80 text-base md:text-lg lg:text-xl max-w-2xl mb-8 md:mb-10 transition-all duration-1000 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {t('hero_subtitle')}
          </p>

          {/* CTA */}
          <div
            className={`flex flex-col sm:flex-row gap-4 mb-10 transition-all duration-1000 delay-600 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <button
              onClick={() => scrollToSection('quote-form')}
              className="inline-flex items-center justify-center gap-2 bg-copper hover:bg-copper-dark text-white font-semibold px-6 py-3.5 rounded-md transition-all duration-300 text-base whitespace-nowrap"
            >
              {t('hero_cta')}
              <i className="ri-arrow-right-line" />
            </button>
          </div>

          {/* Stats */}
          <div
            className={`flex flex-wrap items-center gap-4 md:gap-6 transition-all duration-1000 delay-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                <i className="ri-hammer-line text-copper" />
              </span>
              {t('hero_stat_1')}
            </div>
            <div className="hidden sm:block w-px h-5 bg-white/20" />
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                <i className="ri-shield-check-line text-copper" />
              </span>
              {t('hero_stat_2')}
            </div>
            <div className="hidden sm:block w-px h-5 bg-white/20" />
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10">
                <i className="ri-award-line text-copper" />
              </span>
              {t('hero_stat_3')}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}